import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { HostBottomNav } from '../../components/HostBottomNav';
import NotificationBellButton from '../../components/NotificationBellButton';
import { HostPrimaryButton, hostCardStyle } from '../../components/HostSettingsCard';
import { deleteMyHostListing, fetchMyHostListings } from '../../utils/hostListings';
import { Z_INDICES } from '../../lib/zIndices';

const statusCopy = {
  approved: { label: 'Ready', color: '#178A2F', background: 'rgba(23, 138, 47, 0.10)' },
  pending: { label: 'Reviewing', color: '#8A6717', background: 'rgba(138, 103, 23, 0.12)' },
  rejected: { label: 'Early area', color: '#B34A16', background: 'rgba(179, 74, 22, 0.10)' },
};

export default function HostListingsPage() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removingId, setRemovingId] = useState('');

  const loadListings = async () => {
    try {
      const response = await fetchMyHostListings();
      setListings(response);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to load your listings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadListings();
  }, []);

  const sortedListings = useMemo(
    () => [...listings].sort((left, right) => `${right.createdAt || ''}`.localeCompare(left.createdAt || '')),
    [listings],
  );

  const handleConnectListing = () => {
    // Returning hosts (who already have at least one connected listing) skip the
    // onboarding intro and go straight to the listing URL step. The intro is only
    // for first-time hosts connecting their very first listing.
    if (listings.length) {
      // Stash the origin so /host-verify-listing's back arrow returns to My
      // listings instead of dumping the host back on the intro.
      try {
        sessionStorage.setItem('host_verify_listing_return_to', '/host/listings');
      } catch { /* noop */ }
      navigate('/host-verify-listing');
    } else {
      navigate('/host-intro-1');
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemoval) {
      return;
    }

    setRemovingId(pendingRemoval.id);
    try {
      const response = await deleteMyHostListing(pendingRemoval.id);
      const nextUser = response?.user || null;
      if (nextUser) {
        localStorage.setItem('user', JSON.stringify(nextUser));
      }
      toast.success('Listing removed');
      setPendingRemoval(null);

      // If the removal dropped the user out of the host role (last listing
      // gone), redirect them somewhere sensible instead of blindly
      // dumping them on /guest/home — that was the source of the black
      // screen for admins whose only listing was the "early" one.
      //   • Admin / main-admin → /switch-role so they can hop back to /admin
      //   • Non-admin with other roles available → /switch-role
      //   • Everyone else → /guest/home
      if (nextUser && nextUser.role !== 'host') {
        const isAdmin = Boolean(nextUser.is_admin || nextUser.is_main_admin);
        const availableRoles = Array.isArray(nextUser.available_roles) ? nextUser.available_roles : [];
        const hasAlternativeRole = availableRoles.some((role) => role && role !== 'guest');
        if (isAdmin || hasAlternativeRole) {
          window.location.href = '/switch-role';
        } else {
          window.location.href = '/guest/home';
        }
        return;
      }
      await loadListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to remove this listing');
    } finally {
      setRemovingId('');
    }
  };

  return (
    <div className="app-shell" data-testid="host-my-listings-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <NotificationBellButton buttonTestId="host-my-listings-bell-button" popupTestId="host-my-listings-notification-popup" />
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0, paddingBottom: 'calc(var(--nav-height) + var(--nav-bottom-offset) + 48px + env(safe-area-inset-bottom, 0px))' }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 138, height: 122, objectFit: 'contain' }} />

        <h1 data-testid="host-my-listings-title" style={{ fontFamily: "'Inria Serif', serif", fontSize: 26, fontWeight: 700, color: 'var(--text)', marginTop: 20, marginBottom: 8, textAlign: 'center' }}>My listings</h1>
        <p data-testid="host-my-listings-subtitle" style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.24, margin: '0 0 18px', maxWidth: 320 }}>Only your connected Airbnb listings appear here. Remove old ones anytime or connect a new listing.</p>

        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
          <HostPrimaryButton onClick={handleConnectListing} dataTestId="host-my-listings-connect-button">Connect listing</HostPrimaryButton>
        </div>

        {loading ? <p data-testid="host-my-listings-loading" style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, color: 'var(--text-2)', margin: '8px 0 0' }}>Loading your listings...</p> : null}

        {!loading && !sortedListings.length ? (
          <div data-testid="host-my-listings-empty-state" style={{ ...hostCardStyle, width: '100%', padding: '22px 18px', textAlign: 'center' }}>
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.2 }}>No listings connected yet.</p>
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'var(--text-2)', margin: '10px 0 0', lineHeight: 1.24 }}>Connect your Airbnb listing to start syncing reservations and generating invites.</p>
          </div>
        ) : null}

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sortedListings.map((listing) => {
            const badge = statusCopy[listing.status] || statusCopy.pending;
            return (
              <motion.div key={listing.id} data-testid={`host-my-listings-card-${listing.id}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ ...hostCardStyle, padding: '12px 12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <img src={listing.image} alt={listing.name} data-testid={`host-my-listings-image-${listing.id}`} style={{ width: 112, height: 76, borderRadius: 18, objectFit: 'cover', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p data-testid={`host-my-listings-name-${listing.id}`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1.08, margin: 0 }}>{listing.name}</p>
                    {listing.locationLabel ? <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', margin: '8px 0 0', lineHeight: 1.1 }}>{listing.locationLabel}</p> : null}
                    <div style={{ marginTop: 10 }}>
                      <span data-testid={`host-my-listings-status-${listing.id}`} style={{ display: 'inline-flex', padding: '6px 12px', borderRadius: 9999, background: badge.background, color: badge.color, fontFamily: "'Inria Serif', serif", fontSize: 13, fontWeight: 700 }}>{badge.label}</span>
                    </div>
                  </div>
                </div>

                <button type="button" onClick={() => setPendingRemoval(listing)} disabled={removingId === listing.id} data-testid={`host-my-listings-remove-button-${listing.id}`} style={{ width: '100%', border: '1px solid rgba(221, 6, 8, 0.18)', borderRadius: 9999, background: 'var(--surface)', color: '#DD0608', boxShadow: 'var(--premium-shadow-main)', padding: '12px 14px', fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, cursor: removingId === listing.id ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: removingId === listing.id ? 0.65 : 1 }}>
                  <Trash2 size={16} />
                  {removingId === listing.id ? 'Removing...' : 'Remove listing'}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>

      {pendingRemoval ? (
        <div data-testid="host-my-listings-remove-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(10, 10, 16, 0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', zIndex: Z_INDICES.modalOverlay }}>
          <div style={{ ...hostCardStyle, width: '100%', maxWidth: 340, padding: '22px 18px', textAlign: 'center' }}>
            <p data-testid="host-my-listings-remove-modal-title" style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.16 }}>Are you sure you want to remove this listing?</p>
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'var(--text-2)', margin: '12px 0 0', lineHeight: 1.24 }}>{pendingRemoval.name}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
              <button type="button" onClick={handleConfirmRemove} disabled={removingId === pendingRemoval.id} data-testid="host-my-listings-confirm-remove-button" style={{ width: '100%', borderRadius: 9999, border: 'none', background: '#DD0608', color: '#FFFFFF', padding: '12px 14px', fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, cursor: removingId === pendingRemoval.id ? 'default' : 'pointer', opacity: removingId === pendingRemoval.id ? 0.7 : 1 }}>{removingId === pendingRemoval.id ? 'Removing...' : 'Remove listing'}</button>
              <button type="button" onClick={() => setPendingRemoval(null)} data-testid="host-my-listings-cancel-remove-button" style={{ width: '100%', borderRadius: 9999, border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--text)', boxShadow: 'var(--premium-shadow-main)', padding: '12px 14px', fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}

      <HostBottomNav />
    </div>
  );
}
